import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, X, Phone, HardHat, Package, Loader2, LifeBuoy, StickyNote, CalendarDays, Tag } from "lucide-react";
import { api } from "../../lib/api";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  in_progress: "bg-sky-50 text-sky-800 border-sky-200",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-200",
};
const STATUS_LABEL = { pending: "Da fare", in_progress: "In corso", completed: "Completata" };

export default function InstallerAssistenze({ reloadKey, onNew, onEdit }) {
  const [rows, setRows] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    const h = setTimeout(() => setQ(qInput), 300);
    return () => clearTimeout(h);
  }, [qInput]);

  const load = useCallback(async () => {
    try {
      const params = { task_type: "assistance" };
      if (statusFilter) params.status = statusFilter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/installers/tasks", { params });
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento assistenze");
      setRows([]);
    }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load, reloadKey]);

  async function changeStatus(t, status) {
    try {
      await api.patch(`/installers/tasks/${t.id}/status`, { status });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore cambio stato");
    }
  }

  async function deleteAssist(t) {
    if (!window.confirm(`Eliminare l'assistenza di "${t.title}"?`)) return;
    try {
      await api.delete(`/installers/tasks/${t.id}`);
      toast.success("Assistenza eliminata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  }

  return (
    <div className="space-y-4" data-testid="installers-assistenze">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Cerca assistenze (cliente, indirizzo, telefono, merce…)"
            className="w-full border border-ink/20 pl-9 pr-9 py-2.5 rounded-sm bg-white text-sm"
            data-testid="assistenze-search"
          />
          {qInput && (
            <button onClick={() => setQInput("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-ink" data-testid="assistenze-search-clear"><X size={14} /></button>
          )}
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm" data-testid="assistenze-status-filter">
          <option value="active">Da fare + in corso</option>
          <option value="">Tutte (incl. completate)</option>
          <option value="pending">Da fare</option>
          <option value="in_progress">In corso</option>
          <option value="completed">Completate</option>
        </select>
        <button
          onClick={onNew}
          className="ml-auto inline-flex items-center gap-2 bg-violet-700 hover:bg-violet-800 text-white px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm transition-colors"
          data-testid="assistenze-new-btn"
        >
          <Plus size={14} /> Nuova assistenza
        </button>
      </div>
      <div className="text-[11px] text-slate1">
        Le assistenze non sono calendarizzate: gli operai le vedono sempre nell&apos;app e vengono evidenziate sotto le consegne entro 5 km. Con un tap l&apos;operaio se le auto-assegna.
      </div>

      {!rows ? (
        <div className="flex justify-center py-14 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-ink/10 rounded-sm p-10 text-center text-sm text-slate-400" data-testid="assistenze-empty">
          <LifeBuoy size={26} className="mx-auto mb-2 text-slate-300" />
          {q || statusFilter ? "Nessuna assistenza trovata" : "Nessuna assistenza aperta"}
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => {
            const loaded = (t.goods || []).filter((g) => g.loaded).length;
            const workerNames = (t.workers || []).map((w) => w.name).join(", ");
            return (
              <div key={t.id} className="bg-white border border-ink/10 border-l-4 border-l-violet-600 rounded-sm p-4 flex items-start gap-4 flex-wrap" data-testid={`assistenza-${t.id}`}>
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 bg-violet-50 text-violet-900 border-violet-300">Assistenza</span>
                    <span className="font-medium text-ink">{t.title}</span>
                    <span className={`text-[10px] tracking-wider uppercase font-bold border rounded px-1.5 py-0.5 ${STATUS_STYLE[t.status]}`} data-testid={`assistenza-status-${t.id}`}>{STATUS_LABEL[t.status]}</span>
                  </div>
                  <div className="text-xs text-slate1 mt-1 flex items-center gap-3 flex-wrap">
                    {t.client_address && <span>{t.client_address}</span>}
                    {t.client_phone && <span className="inline-flex items-center gap-1"><Phone size={10} /> {t.client_phone}</span>}
                    {t.riferimento && <span className="inline-flex items-center gap-1 font-medium text-ink"><Tag size={10} /> Rif. {t.riferimento}</span>}
                    <span className="inline-flex items-center gap-1" data-testid={`assistenza-date-${t.id}`}>
                      <CalendarDays size={10} />
                      {t.scheduled_date
                        ? `${new Date(`${t.scheduled_date}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short" })}${t.scheduled_time ? ` ore ${t.scheduled_time}` : ""}`
                        : "Non pianificata"}
                    </span>
                  </div>
                  {t.client_delivery_notes && (
                    <div className="text-[11px] text-amber-900 mt-1 inline-flex items-start gap-1.5 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1">
                      <StickyNote size={11} className="shrink-0 mt-0.5 text-amber-600" /> {t.client_delivery_notes}
                    </div>
                  )}
                  <div className="text-xs text-slate1 mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1" data-testid={`assistenza-workers-${t.id}`}><HardHat size={11} /> {workerNames || "Libera — prendibile dagli operai"}</span>
                    {(t.goods || []).length > 0 && <span className="inline-flex items-center gap-1"><Package size={11} /> Merce {loaded}/{(t.goods || []).length}</span>}
                    <span>📷 {t.photo_count} · 📝 {t.note_count} · 📎 {t.attachment_count}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={t.status}
                    onChange={(e) => changeStatus(t, e.target.value)}
                    className="border border-ink/15 px-2 py-1.5 rounded-sm bg-cream text-xs"
                    data-testid={`assistenza-status-select-${t.id}`}
                  >
                    <option value="pending">Da fare</option>
                    <option value="in_progress">In corso</option>
                    <option value="completed">Completata</option>
                  </select>
                  <button onClick={() => onEdit(t)} className="p-2 text-slate1 hover:text-ink rounded-sm hover:bg-ink/5" title="Modifica" data-testid={`assistenza-edit-${t.id}`}><Pencil size={15} /></button>
                  <button onClick={() => deleteAssist(t)} className="p-2 text-rose-600 hover:text-rose-700 rounded-sm hover:bg-rose-50" title="Elimina" data-testid={`assistenza-del-${t.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
